import { useMessageStore } from "@/state/stores";
import { useCallback } from "react";

/**
 * Hook for handling WebSocket errors with automatic toast notifications.
 *
 * @returns A function to return the current error, or null if no error exists.
 *
 * Usage:
 * ```tsx
 * const getCurrentError = useCurrentWebSocketError();
 *
 * useEffect(() => {
 *   const error = getCurrentError?.(); // Only calls if error exists
 *   if (error) {
 *     toast({
 *       variant: "destructive",
 *       title: "Chat Error",
 *       description: error.message,
 *       duration: 5000,
 *     });
 *   }
 * }, [getCurrentError]);
 * ```
 *
 * When to use:
 * - In components that need to display WebSocket error notifications
 * - When you want centralized error handling with consistent toast styling
 * - Prefer this over directly accessing webSocketError state for UI display
 */
export function useCurrentWebSocketError() {
  const webSocketError = useMessageStore((state) => state.webSocketError);
  const setWebSocketError = useMessageStore((state) => state.setWebSocketError);

  // Return function only when error exists, null otherwise
  return useCallback(() => {
    if (!webSocketError) {
      return null;
    }

    setWebSocketError(null);
    return webSocketError;
  }, [webSocketError, setWebSocketError]);
}
